import React, { ReactElement, useEffect, useReducer, useState } from 'react';

import LayoutPupDocs from 'pupdocs/components/layout/LayoutPupDocs';
import { FlightFilter } from 'pupdocs/pages/preFlights/flightFilter/FlightFilter';
import PreFlightsTable from 'pupdocs/pages/preFlights/preFlightsTable/PreFlightsTable';
import { useLayoutState } from 'shared/components/layout/useLayoutState';
import { apiPreFlights } from 'utilities/http/api/pupDocs/apiPreFlights';
import { log } from 'utilities/log/log';
import { notify } from 'utilities/notifications/notify';
import { BreadcrumbItem } from 'types';
import { PreFlightsFilters, PreFlightsTitle } from './PreFlights.styles';
import {
  metaStatus,
  MetaStatusString,
  preFlightInitialState,
  preFlightReducer,
} from './preFlights.state';
import { getPreFlightsFacets, mapPreFlights } from './utilities/preFlight.utilities';

const breadcrumbs: BreadcrumbItem[] = [
  {
    key: 'main',
    label: 'Main',
    path: '/',
  },
  {
    key: 'pupDocs',
    label: 'PupDocs',
  },
  {
    key: 'preFlight',
    label: 'Pre-Flight',
  },
];

interface StatusCounter {
  status: MetaStatusString;
  counter: number;
}

export function PreFlights(): ReactElement {
  const [state, dispatch] = useReducer(preFlightReducer, preFlightInitialState);
  const [counters, setCounters] = useState<StatusCounter[]>([]);
  const [currentMetaFilter, setCurrentMetaFilter] = useState('isUrgent');
  const { dispatch: dispatchLayout } = useLayoutState();

  useEffect(() => {
    dispatchLayout({ type: 'SET_BREADCRUMBS', breadcrumbs });
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiPreFlights.getPreFlights();
        dispatch({
          type: 'SET_PRE_FLIGHTS',
          payload: {
            facets: getPreFlightsFacets(res?.json.lineItems),
            preFlights: mapPreFlights(res?.json.lineItems),
          },
        });

        if (!res) {
          notify.error('Could not load any document.');
        }
      } catch (e) {
        log.debug(e);
        notify.error('Could not load any document.');
      }
    };

    fetch();
    dispatch({ type: 'LOAD_PRE_FLIGHTS' });
  }, []);

  const handleFilterByStatus = (status: string) => {
    const fetch = async () => {
      try {
        const res = await apiPreFlights.getPreFlights();
        dispatch({
          type: 'SET_PRE_FLIGHTS',
          payload: {
            facets: getPreFlightsFacets(res?.json.lineItems),
            preFlights: mapPreFlights(res?.json.lineItems),
          },
        });
        dispatch({
          type: 'FILTER_PRE_FLIGHTS_META_STATUS',
          payload: {
            metaStatus: status as MetaStatusString,
          },
        });
        setCurrentMetaFilter(status);

        if (!res) {
          notify.error('Could not load any document.');
        }
      } catch (e) {
        log.debug(e);
        notify.error('Could not load any document.');
      }
    };
    fetch();
  };

  useEffect(() => {
    if (counters.length === 0 && state.preFlights.length > 0) {
      getCounters();
      handleFilterByStatus('isUrgent');
    }
  }, [state.preFlights]);

  const getCounters = (): void => {
    const statusesCounter: StatusCounter[] = [];
    const statuses = [
      'isUrgent',
      'isPendingReview',
      'isInReview',
      'isEmpty',
      'isFinalized',
    ];
    statuses.forEach((status) => {
      statusesCounter.push({
        status: status as MetaStatusString,
        counter: state.preFlights.filter(
          (item) => item.meta && item.meta[status as MetaStatusString] === true,
        ).length,
      });
    });

    setCounters(statusesCounter);
  };

  return (
    <LayoutPupDocs>
      <PreFlightsTitle>Pre-Flight</PreFlightsTitle>
      <PreFlightsFilters>
        <FlightFilter
          status="Urgent"
          description="Travel or expiration"
          current={currentMetaFilter === metaStatus.urgent}
          onFilter={() => handleFilterByStatus(metaStatus.urgent)}
          totalDocsMatching={
            counters.find((c) => c.status === metaStatus.urgent)?.counter ?? 0
          }
        />
        <FlightFilter
          status="Pending Review"
          description="Uploaded docs"
          current={currentMetaFilter === metaStatus.pending}
          onFilter={() => handleFilterByStatus(metaStatus.pending)}
          totalDocsMatching={
            counters.find((c) => c.status === metaStatus.pending)?.counter ?? 0
          }
        />
        <FlightFilter
          status="In-review"
          description="Docs in progress"
          current={currentMetaFilter === metaStatus.review}
          onFilter={() => handleFilterByStatus(metaStatus.review)}
          totalDocsMatching={
            counters.find((c) => c.status === metaStatus.review)?.counter ?? 0
          }
        />
        <FlightFilter
          status="Empty"
          description="Pups with no docs"
          current={currentMetaFilter === metaStatus.empty}
          onFilter={() => handleFilterByStatus(metaStatus.empty)}
          totalDocsMatching={
            counters.find((c) => c.status === metaStatus.empty)?.counter ?? 0
          }
        />

        <FlightFilter
          status="Finalized"
          description="Ready to travel"
          onFilter={() => handleFilterByStatus(metaStatus.finalized)}
          totalDocsMatching={
            counters.find((c) => c.status === metaStatus.finalized)?.counter ?? 0
          }
        />
      </PreFlightsFilters>
      <PreFlightsTable preFlightsState={state} dispatch={dispatch} />
    </LayoutPupDocs>
  );
}
